// ElevenLabs AI Agent integration — one Business = one Agent ID.
// Flow: API Key → Agent ID → Verify Agent → Save Configuration → Sync.
// The Agent ID is verified against ElevenLabs (server-side) before it can be
// saved, and conversation sync is disabled until a verified Agent exists.
// The API key is write-only: the backend only ever reports has_api_key.

import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  AudioLines, CheckCircle, XCircle, RefreshCw, Loader2, Unplug,
  BadgeCheck, AlertTriangle, Bot,
} from "lucide-react";
import { useBusiness } from "@/contexts/BusinessContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ElevenLabsIntegration({ onChanged }) {
  const { businessId } = useBusiness();
  const [settings, setSettings] = useState(null);
  const [apiKeyInput, setApiKeyInput] = useState("");
  const [agentIdInput, setAgentIdInput] = useState("");
  const [verified, setVerified] = useState(null);   // { agent_id, name, status }
  const [pollEnabled, setPollEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  const qs = () => (businessId ? `?business_id=${businessId}` : "");

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/ai-conversations/settings${qs()}`, { credentials: "include" });
      if (r.ok) {
        const d = await r.json();
        setSettings(d);
        setAgentIdInput(d.elevenlabs_agent_id || "");
        setPollEnabled(d.poll_enabled !== false);
        setVerified(d.elevenlabs_agent_id
          ? { agent_id: d.elevenlabs_agent_id, name: d.elevenlabs_agent_name, status: d.elevenlabs_agent_status }
          : null);
      }
    } finally { setLoading(false); }
  }, [businessId]); // eslint-disable-line

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  // Typing a different Agent ID invalidates a previous verification.
  useEffect(() => {
    if (verified && agentIdInput.trim() !== verified.agent_id) setVerified(null);
  }, [agentIdInput]); // eslint-disable-line

  const handleVerify = async () => {
    const agentId = agentIdInput.trim();
    if (!agentId) { toast.error("Enter the ElevenLabs Agent ID"); return; }
    if (!apiKeyInput.trim() && !settings?.has_api_key) { toast.error("Enter your ElevenLabs API key first"); return; }
    setVerifying(true);
    setVerified(null);
    try {
      const body = { agent_id: agentId };
      if (apiKeyInput.trim()) body.api_key = apiKeyInput.trim();
      const r = await fetch(`${BACKEND_URL}/api/ai-conversations/verify-agent${qs()}`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const d = await r.json().catch(() => ({}));
      if (r.ok && d.ok) { setVerified(d.agent); toast.success(`Agent verified — ${d.agent.name}`); }
      else toast.error(d.detail || "Agent verification failed");
    } catch { toast.error("Verification failed"); }
    finally { setVerifying(false); }
  };

  const handleSave = async () => {
    if (!verified) { toast.error("Verify the Agent ID before saving"); return; }
    setSaving(true);
    try {
      const body = {
        elevenlabs_agent_id: verified.agent_id,
        elevenlabs_agent_name: verified.name,
        poll_enabled: pollEnabled,
        business_id: businessId,
      };
      if (apiKeyInput.trim()) body.elevenlabs_api_key = apiKeyInput.trim();
      const r = await fetch(`${BACKEND_URL}/api/ai-conversations/settings${qs()}`, {
        method: "PUT", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const d = await r.json().catch(() => ({}));
      if (r.ok) { toast.success("ElevenLabs configuration saved"); setApiKeyInput(""); fetchSettings(); onChanged && onChanged(); }
      else toast.error(d.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const handleSync = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const r = await fetch(`${BACKEND_URL}/api/ai-conversations/sync-now${qs()}`, { method: "POST", credentials: "include" });
      const d = await r.json().catch(() => ({}));
      if (r.ok && d.ok !== false) {
        setSyncResult(d);
        toast.success(`Sync completed — ${d.new_conversations || 0} new, ${d.updated || 0} updated`);
      } else toast.error(d.error || d.detail || "Sync failed");
      fetchSettings();
    } finally { setSyncing(false); }
  };

  const handleDisconnect = async () => {
    if (!window.confirm("Disconnect ElevenLabs? Conversation syncing stops; existing conversations stay in the CRM.")) return;
    const r = await fetch(`${BACKEND_URL}/api/ai-conversations/settings${qs()}`, {
      method: "PUT", credentials: "include", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ elevenlabs_api_key: "", elevenlabs_agent_id: "", elevenlabs_agent_name: "", poll_enabled: false, agent_ids: [], business_id: businessId }),
    });
    if (r.ok) { toast.success("Disconnected"); setVerified(null); setAgentIdInput(""); setSyncResult(null); fetchSettings(); onChanged && onChanged(); }
    else toast.error("Disconnect failed");
  };

  if (loading) return <div className="p-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;

  const connected = !!settings?.has_api_key;
  const syncEnabled = !!settings?.sync_enabled;
  const needsAgent = connected && !syncEnabled;

  return (
    <div className="space-y-5" data-testid="elevenlabs-integration">
      {/* Status strip */}
      <div className={`flex items-center justify-between gap-3 flex-wrap rounded-xl border p-4 ${syncEnabled ? "bg-emerald-50 border-emerald-200" : connected ? "bg-amber-50 border-amber-200" : "bg-slate-50 border-slate-200"}`}>
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${syncEnabled ? "bg-emerald-100" : connected ? "bg-amber-100" : "bg-slate-200"}`}>
            <AudioLines className={`w-5 h-5 ${syncEnabled ? "text-emerald-600" : connected ? "text-amber-600" : "text-slate-500"}`} />
          </div>
          <div>
            <div className="font-semibold text-slate-900 flex items-center gap-2 flex-wrap">
              SIYA AI Agent
              {syncEnabled
                ? <Badge className="bg-emerald-100 text-emerald-700"><CheckCircle className="w-3 h-3 mr-1" /> Connected</Badge>
                : connected
                  ? <Badge className="bg-amber-100 text-amber-700"><AlertTriangle className="w-3 h-3 mr-1" /> Agent required</Badge>
                  : <Badge className="bg-slate-100 text-slate-600"><XCircle className="w-3 h-3 mr-1" /> Not Connected</Badge>}
            </div>
            <div className="text-xs text-slate-500 mt-0.5">
              {syncEnabled ? (
                <>
                  <b>Agent:</b> {settings.elevenlabs_agent_name || "—"} · <span className="font-mono">{settings.elevenlabs_agent_id}</span>
                  {settings.last_poll_at ? <> · <b>Last sync:</b> {new Date(settings.last_poll_at).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</> : null}
                </>
              ) : connected
                ? "API key stored — add and verify one Agent ID to enable conversation sync."
                : "Connect a SIYA AI Agent to sync voice conversations into the CRM."}
            </div>
          </div>
        </div>
        {connected && (
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={handleSync} disabled={syncing || !syncEnabled} title={syncEnabled ? "" : "Configure an Agent ID first"} data-testid="el-sync-now">
              {syncing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />} Sync now
            </Button>
            <Button size="sm" variant="outline" className="text-rose-600 border-rose-200" onClick={handleDisconnect} data-testid="el-disconnect">
              <Unplug className="w-4 h-4 mr-1" /> Disconnect
            </Button>
          </div>
        )}
      </div>

      {/* Migration notice — existing config without a single agent */}
      {needsAgent && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900" data-testid="el-migration-notice">
          <b>Action needed:</b> this business previously synced multiple agents. Conversation sync is
          paused until you specify <b>one</b> Agent ID below and verify it. Existing conversations are
          preserved and stay visible.
        </div>
      )}

      {/* Sync result */}
      {syncResult && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900" data-testid="el-sync-result">
          <div className="font-semibold flex items-center gap-1.5"><CheckCircle className="w-4 h-4" /> Sync completed successfully</div>
          <div className="mt-1 grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-0.5 text-[13px]">
            <div><span className="text-emerald-700/70">Agent:</span> {syncResult.agent_name || settings?.elevenlabs_agent_name || "—"}</div>
            <div><span className="text-emerald-700/70">Checked:</span> {syncResult.checked ?? 0}</div>
            <div><span className="text-emerald-700/70">New:</span> <b>{syncResult.new_conversations ?? 0}</b></div>
            <div><span className="text-emerald-700/70">Updated:</span> {syncResult.updated ?? 0}</div>
            <div><span className="text-emerald-700/70">Already synced:</span> {syncResult.already_synced ?? 0}</div>
          </div>
        </div>
      )}

      {/* Configuration */}
      <div className="grid gap-4 max-w-xl">
        <div>
          <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">ElevenLabs API Key</Label>
          <Input
            type="password"
            className="mt-1"
            placeholder={connected ? "•••••••••••••••••• (stored — paste a new key to replace)" : "sk_… from elevenlabs.io → Profile → API keys"}
            value={apiKeyInput}
            onChange={e => setApiKeyInput(e.target.value)}
            data-testid="el-api-key"
          />
          <p className="text-[11px] text-slate-400 mt-1">Stored server-side only — never shown again after saving.</p>
        </div>

        <div>
          <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
            ElevenLabs Agent ID <span className="text-rose-500">*</span>
          </Label>
          <div className="flex gap-2 mt-1">
            <Input
              placeholder="Enter your ElevenLabs Agent ID"
              value={agentIdInput}
              onChange={e => setAgentIdInput(e.target.value)}
              className="font-mono text-[13px]"
              data-testid="el-agent-id"
            />
            <Button variant="outline" onClick={handleVerify} disabled={verifying} className="shrink-0" data-testid="el-verify-agent">
              {verifying ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <BadgeCheck className="w-4 h-4 mr-1" />} Verify Agent
            </Button>
          </div>
          <p className="text-[11px] text-slate-400 mt-1">One business connects to exactly one agent. Conversations sync from this agent only.</p>
        </div>

        {/* Verified agent card */}
        {verified && (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3" data-testid="el-agent-verified">
            <div className="flex items-center gap-2 text-emerald-800 font-semibold text-sm">
              <CheckCircle className="w-4 h-4" /> Agent Verified
            </div>
            <div className="mt-2 flex items-start gap-2.5">
              <Bot className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
              <div className="min-w-0 text-[13px]">
                <div className="font-semibold text-slate-800">{verified.name}</div>
                <div className="font-mono text-[11px] text-slate-500 truncate">{verified.agent_id}</div>
                {verified.status && <div className="text-[11px] text-emerald-700 mt-0.5">Status: {verified.status}</div>}
              </div>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between border rounded-xl p-3">
          <div>
            <div className="text-sm font-medium text-slate-800">Auto-sync conversations</div>
            <div className="text-[11px] text-slate-400">Poll ElevenLabs every 15 minutes for this agent's new conversations</div>
          </div>
          <Switch checked={pollEnabled} onCheckedChange={setPollEnabled} data-testid="el-poll-toggle" />
        </div>

        <Button
          onClick={handleSave}
          disabled={saving || !verified}
          title={verified ? "" : "Verify the Agent ID first"}
          className="bg-indigo-600 hover:bg-indigo-700 text-white w-fit"
          data-testid="el-save"
        >
          {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null} Save Configuration
        </Button>
      </div>
    </div>
  );
}
